// Zvonalitik Profiles Store Module
// Manages analysis profiles for call analytics (system profiles, user profiles, settings)
import axiosConfig from '@/lib/axiosConfig';

const state = {
  // User profiles (editable)
  profiles: [],

  // System profiles (read-only, shared across all users)
  systemProfiles: [],

  // Call purposes dictionary: {alias: caption}
  callPurposes: {},

  // Profile settings per call purpose: {call_purpose: profile_id}
  profileSettings: {},

  // Profile currently being edited
  currentProfile: null,

  // Loading states
  loading: false,
  error: null
};

const getters = {
  /**
   * Get all profiles (system + user)
   */
  allProfiles: (state) => [...state.systemProfiles, ...state.profiles],

  /**
   * Get profiles filtered by call purpose
   */
  profilesByPurpose: (state) => (purpose) => {
    const all = [...state.systemProfiles, ...state.profiles];
    return purpose ? all.filter(p => p.call_purpose === purpose) : all;
  },

  /**
   * Get profile by ID (searches both system and user profiles)
   */
  getProfileById: (state) => (id) => {
    return state.profiles.find(p => p.profile_id === id)
      || state.systemProfiles.find(p => p.profile_id === id);
  },

  /**
   * Get default profile ID for a given call purpose
   */
  defaultProfileForPurpose: (state) => (purpose) => {
    return state.profileSettings[purpose] || null;
  },

  /**
   * Get user profiles only
   */
  getUserProfiles: (state) => state.profiles,

  /**
   * Get system profiles only
   */
  getSystemProfiles: (state) => state.systemProfiles,

  /**
   * Get call purposes dictionary
   */
  getCallPurposes: (state) => state.callPurposes,

  /**
   * Get call purposes as array of {alias, caption}
   */
  getCallPurposesArray: (state) => {
    return Object.entries(state.callPurposes).map(([alias, caption]) => ({
      alias,
      caption
    }));
  },

  /**
   * Get profile settings
   */
  getProfileSettings: (state) => state.profileSettings,

  /**
   * Get current profile being edited
   */
  getCurrentProfile: (state) => state.currentProfile,

  /**
   * Check if loading
   */
  isLoading: (state) => state.loading,

  /**
   * Get error message
   */
  getError: (state) => state.error
};

const mutations = {
  SET_PROFILES(state, profiles) {
    state.profiles = profiles;
  },

  SET_SYSTEM_PROFILES(state, profiles) {
    state.systemProfiles = profiles;
  },

  SET_CALL_PURPOSES(state, purposes) {
    state.callPurposes = purposes;
  },

  SET_PROFILE_SETTINGS(state, settings) {
    state.profileSettings = settings;
  },

  SET_CURRENT_PROFILE(state, profile) {
    state.currentProfile = profile;
  },

  SET_LOADING(state, loading) {
    state.loading = loading;
  },

  SET_ERROR(state, error) {
    state.error = error;
  },

  CLEAR_ERROR(state) {
    state.error = null;
  },

  ADD_PROFILE(state, profile) {
    state.profiles.push(profile);
  },

  UPDATE_PROFILE(state, profile) {
    const idx = state.profiles.findIndex(p => p.profile_id === profile.profile_id);
    if (idx !== -1) {
      state.profiles.splice(idx, 1, profile);
    }
  },

  REMOVE_PROFILE(state, profileId) {
    state.profiles = state.profiles.filter(p => p.profile_id !== profileId);
  }
};

const actions = {
  /**
   * Fetch all profiles (both system and user profiles)
   * @param {boolean} includeSystem - Whether to include system profiles (default: true)
   */
  async fetchProfiles({ commit, rootState }, { includeSystem = true } = {}) {
    commit('SET_LOADING', true);
    commit('CLEAR_ERROR');

    try {
      const params = {
        action: 'zvonalitik_get_profiles',
        include_system: includeSystem ? 1 : 0
      };

      // Add chatbot_name for JWT auth
      if (rootState.app && rootState.app.configData && rootState.app.configData.chatbot_name) {
        params.chatbot_name = rootState.app.configData.chatbot_name;
      }

      const response = await axiosConfig.get('', { params });
      const data = response.data;

      if (data.success === false) {
        throw new Error(data.error || 'Failed to fetch profiles');
      }

      if (data.profiles) {
        commit('SET_PROFILES', data.profiles.filter(p => !p.is_system));
        commit('SET_SYSTEM_PROFILES', data.profiles.filter(p => p.is_system));
      }

      return data;
    } catch (error) {
      console.error('Error fetching zvonalitik profiles:', error);
      commit('SET_ERROR', error.message || 'Failed to load profiles');
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  /**
   * Fetch single profile by ID
   * @param {number|string} profileId - Profile ID to fetch
   */
  async fetchProfile({ commit, rootState }, profileId) {
    commit('SET_LOADING', true);
    commit('CLEAR_ERROR');

    try {
      const params = {
        action: 'zvonalitik_get_profile',
        profile_id: profileId
      };

      if (rootState.app && rootState.app.configData && rootState.app.configData.chatbot_name) {
        params.chatbot_name = rootState.app.configData.chatbot_name;
      }

      const response = await axiosConfig.get('', { params });

      if (response.data.success === false) {
        throw new Error(response.data.error || 'Failed to fetch profile');
      }

      const profile = response.data.profile || response.data;
      commit('SET_CURRENT_PROFILE', profile);
      return profile;
    } catch (error) {
      console.error('Error fetching zvonalitik profile:', error);
      commit('SET_ERROR', error.message || 'Failed to fetch profile');
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  /**
   * Save profile (create or update)
   * @param {Object} profile - Profile data to save
   */
  async saveProfile({ commit, rootState }, profile) {
    commit('SET_LOADING', true);
    commit('CLEAR_ERROR');

    try {
      // Build JSON payload (required for nested categories/criteria)
      const payload = { ...profile };

      // Add chatbot_name
      if (rootState.app && rootState.app.configData && rootState.app.configData.chatbot_name) {
        payload.chatbot_name = rootState.app.configData.chatbot_name;
      }

      // Action in query params (backend reads from GET/POST), data in JSON body
      const response = await axiosConfig.post('?action=zvonalitik_save_profile', JSON.stringify(payload), {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.data.success === false) {
        throw new Error(response.data.error || 'Failed to save profile');
      }

      const savedProfile = response.data.profile || response.data;

      if (profile.profile_id) {
        commit('UPDATE_PROFILE', savedProfile);
      } else {
        commit('ADD_PROFILE', savedProfile);
      }

      return savedProfile;
    } catch (error) {
      console.error('Error saving zvonalitik profile:', error);
      commit('SET_ERROR', error.message || 'Failed to save profile');
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  /**
   * Delete profile
   * @param {number|string} profileId - Profile ID to delete
   */
  async deleteProfile({ commit, rootState }, profileId) {
    commit('SET_LOADING', true);
    commit('CLEAR_ERROR');

    try {
      const params = new URLSearchParams();
      params.append('action', 'zvonalitik_delete_profile');
      params.append('profile_id', profileId);

      if (rootState.app && rootState.app.configData && rootState.app.configData.chatbot_name) {
        params.append('chatbot_name', rootState.app.configData.chatbot_name);
      }

      const response = await axiosConfig.post('', params, {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        }
      });

      if (response.data.success === false) {
        throw new Error(response.data.error || 'Failed to delete profile');
      }

      commit('REMOVE_PROFILE', profileId);
      return response.data;
    } catch (error) {
      console.error('Error deleting zvonalitik profile:', error);
      commit('SET_ERROR', error.message || 'Failed to delete profile');
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  /**
   * Duplicate profile
   * @param {number|string} profileId - Profile ID to duplicate
   */
  async duplicateProfile({ commit, rootState }, profileId) {
    commit('SET_LOADING', true);
    commit('CLEAR_ERROR');

    try {
      const params = new URLSearchParams();
      params.append('action', 'zvonalitik_duplicate_profile');
      params.append('profile_id', profileId);

      if (rootState.app && rootState.app.configData && rootState.app.configData.chatbot_name) {
        params.append('chatbot_name', rootState.app.configData.chatbot_name);
      }

      const response = await axiosConfig.post('', params, {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        }
      });

      if (response.data.success === false) {
        throw new Error(response.data.error || 'Failed to duplicate profile');
      }

      const duplicatedProfile = response.data.profile || response.data;
      commit('ADD_PROFILE', duplicatedProfile);
      return duplicatedProfile;
    } catch (error) {
      console.error('Error duplicating zvonalitik profile:', error);
      commit('SET_ERROR', error.message || 'Failed to duplicate profile');
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  /**
   * Fetch call purposes dictionary
   */
  async fetchCallPurposes({ commit, rootState }) {
    try {
      const params = {
        action: 'zvonalitik_get_call_purposes'
      };

      if (rootState.app && rootState.app.configData && rootState.app.configData.chatbot_name) {
        params.chatbot_name = rootState.app.configData.chatbot_name;
      }

      const response = await axiosConfig.get('', { params });

      if (response.data.success === false) {
        throw new Error(response.data.error || 'Failed to fetch call purposes');
      }

      const purposes = response.data.call_purposes || {};
      commit('SET_CALL_PURPOSES', purposes);
      return purposes;
    } catch (error) {
      console.error('Error fetching call purposes:', error);
      commit('SET_ERROR', error.message || 'Failed to fetch call purposes');
      throw error;
    }
  },

  /**
   * Fetch profile settings (default profile per call purpose)
   */
  async fetchProfileSettings({ commit, rootState }) {
    try {
      const params = {
        action: 'zvonalitik_get_profile_settings'
      };

      if (rootState.app && rootState.app.configData && rootState.app.configData.chatbot_name) {
        params.chatbot_name = rootState.app.configData.chatbot_name;
      }

      const response = await axiosConfig.get('', { params });

      if (response.data.success === false) {
        throw new Error(response.data.error || 'Failed to fetch profile settings');
      }

      // Convert array of settings to object {call_purpose: profile_id}
      const settings = {};
      (response.data.settings || []).forEach(s => {
        settings[s.call_purpose] = s.profile_id;
      });

      commit('SET_PROFILE_SETTINGS', settings);
      return settings;
    } catch (error) {
      console.error('Error fetching profile settings:', error);
      commit('SET_ERROR', error.message || 'Failed to fetch profile settings');
      throw error;
    }
  },

  /**
   * Set profile setting (default profile for a call purpose)
   * @param {string} callPurpose - Call purpose alias
   * @param {number|string} profileId - Profile ID to set as default
   */
  async setProfileSetting({ commit, state, rootState }, { callPurpose, profileId }) {
    commit('SET_LOADING', true);
    commit('CLEAR_ERROR');

    try {
      const params = new URLSearchParams();
      params.append('action', 'zvonalitik_set_profile_setting');
      params.append('call_purpose', callPurpose);
      params.append('profile_id', profileId);

      if (rootState.app && rootState.app.configData && rootState.app.configData.chatbot_name) {
        params.append('chatbot_name', rootState.app.configData.chatbot_name);
      }

      const response = await axiosConfig.post('', params, {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        }
      });

      if (response.data.success === false) {
        throw new Error(response.data.error || 'Failed to save profile setting');
      }

      // Update local state
      const settings = { ...state.profileSettings, [callPurpose]: profileId };
      commit('SET_PROFILE_SETTINGS', settings);
      return response.data;
    } catch (error) {
      console.error('Error setting profile setting:', error);
      commit('SET_ERROR', error.message || 'Failed to save profile setting');
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  /**
   * Initialize profiles data (profiles + call purposes + settings)
   */
  async initProfilesData({ dispatch }) {
    return Promise.all([
      dispatch('fetchProfiles'),
      dispatch('fetchCallPurposes'),
      dispatch('fetchProfileSettings')
    ]);
  },

  /**
   * Clear current profile being edited
   */
  clearCurrentProfile({ commit }) {
    commit('SET_CURRENT_PROFILE', null);
  },

  /**
   * Clear error state
   */
  clearError({ commit }) {
    commit('CLEAR_ERROR');
  },

  /**
   * Clear all profiles data (on logout)
   */
  clearProfilesData({ commit }) {
    commit('SET_PROFILES', []);
    commit('SET_SYSTEM_PROFILES', []);
    commit('SET_CALL_PURPOSES', {});
    commit('SET_PROFILE_SETTINGS', {});
    commit('SET_CURRENT_PROFILE', null);
    commit('CLEAR_ERROR');
  }
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
};
